import React, { useState } from 'react';
import { connect } from 'react-redux';
import cn from 'classnames';
import { bindActionCreators } from 'redux';

import beSaveTimeOffAllowance from 'actions/timeoff/beSaveTimeOffAllowance.action';
import StaffAllowanceInput from 'components/hr/timeOffStaff/StaffAllowanceInput.component';
import { useTranslation } from 'providers/GILT.provider';
import { selectCurrentSalonId } from 'selectors/currentSalon.selectors';
import { selectTimeOffAllowances, selectTimeOffCategories } from 'selectors/timeoff.selectors';
import asArray from 'utils/arrays/asArray.util';
import fromAnyToNumber from 'utils/converters/fromAnyToNumber.util';
import call from 'utils/functions/call.util';
import asObject from 'utils/objects/asObject.util';
import isFalsy from 'utils/predicates/isFalsy.util';
import C from 'utils/values/constant.util';

import CN from './StaffTimeOffAllowances.module.scss';

const ALERT_TYPES = C({
  success: 'success',
  info: 'info',
  danger: 'danger',
});

const getAlertMessage = (t) =>
  C({
    save: t('humanResources.timeOffSettings.allowancesPerStaff.alertMessages.save'),
    saveFail: t('humanResources.timeOffSettings.allowancesPerStaff.alertMessages.saveFail'),
    processing: t('humanResources.timeOffSettings.allowancesPerStaff.alertMessages.processing'),
    error: t('humanResources.timeOffSettings.allowancesPerStaff.alertMessages.error'),
  });

const getExistingAllowanceId = ({ allowances, currAllowance }) => {
  const { time_off_category_id: categoryId, stylist_id: stylistId } = asObject(currAllowance);

  const found = allowances.find(({ time_off_category_id: allowanceCategoryId, stylist_id: allowanceStylistId }) => allowanceCategoryId === categoryId && allowanceStylistId === stylistId);

  return asObject(found).id;
};

const StaffTimeOffAllowance = ({ salonId, allowance, stylist, index, onAlert, categories: categoriesFromProps, allowances: allowancesFromProps, beSaveTimeOffAllowance }) => {
  const { t } = useTranslation();
  const categories = asArray(categoriesFromProps);
  const allowances = asArray(allowancesFromProps);
  const [selectedIndex, setSelectedIndex] = useState(-1);

  const handleSelect = ({ index }) => void setSelectedIndex(index);

  const alertMessage = getAlertMessage(t);

  const handleBlur = ({ allowance, stylist, category, isDirty }) => {
    setSelectedIndex();
    if (!isDirty) return;

    const currAllowance = {
      time_off_category_id: category.id,
      stylist_id: stylist.id,
      allowance: fromAnyToNumber(allowance.allowance),
    };

    const existingAllowanceId = getExistingAllowanceId({ allowances, currAllowance });

    const options = {
      salonId,
      isNew: isFalsy(existingAllowanceId),
      allowance: { ...currAllowance, id: existingAllowanceId },
    };

    call('onAlert', onAlert, { type: ALERT_TYPES.info, message: alertMessage.processing });

    beSaveTimeOffAllowance(options)
      .then(({ response, error }) => {
        if (!response && !error) return;

        const options = error ? { type: ALERT_TYPES.danger, message: alertMessage.saveFail } : { type: ALERT_TYPES.success, message: alertMessage.save };

        call('onAlert', onAlert, options);
      })
      .catch((e) => {
        call('onAlert', onAlert, { type: ALERT_TYPES.danger, message: alertMessage.error });
        throw e;
      });
  };

  return (
    <div
      data-bem="StaffTimeOffAllowances"
      className={cn({
        [CN.stylistRow]: true,
        [CN.focusIn]: index === selectedIndex,
      })}
    >
      <div data-bem="StaffTimeOffAllowances__list" className={CN.list}>
        <div className={CN.staffName}>{stylist.user_name}</div>
        {categories.map((c) => (
          <div key={c.id}>
            <StaffAllowanceInput index={index} allowance={allowance[c.id]} category={c} stylist={stylist} onChange={handleSelect} onBlur={handleBlur} onFocus={handleSelect} />
          </div>
        ))}
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  salonId: selectCurrentSalonId(state),
  categories: selectTimeOffCategories(state),
  allowances: selectTimeOffAllowances(state),
});

const mapDispatchToProps = (dispatch) => bindActionCreators({ beSaveTimeOffAllowance }, dispatch);

const StaffTimeOffAllowanceHoc = connect(mapStateToProps, mapDispatchToProps)(StaffTimeOffAllowance);

export default StaffTimeOffAllowanceHoc;
